// English QMS Implementation & Remediation page, built on the Inspection Readiness design family.
import { useState } from "react";
import { Link, Navigate } from "@/lib/router-compat";
import { ArrowDown, ArrowRight, Building2, CheckCircle2, Eye, Puzzle, RefreshCw } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { Button } from "@/components/ui/button";
import { getServiceBySlug, type ServiceData } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import { cn } from "@/lib/utils";
import Eyebrow from "@/components/services/SectionEyebrow";
import heroImg from "@/assets/services/qms/hero.jpg";
import essentialsImg from "@/assets/services/qms/essentials.jpg";
import panelImplImg from "@/assets/services/qms/panel-implementation.jpg";
import panelRemImg from "@/assets/services/qms/panel-remediation.jpg";
import qmsrImg from "@/assets/services/qms/qmsr.jpg";
import processImg from "@/assets/services/qms/process.jpg";
import whyImg from "@/assets/services/qms/why.jpg";

export const QMS_META_TITLE = "QMS Development & Optimization | QbD Group";
export const QMS_META_DESCRIPTION =
  "Practical QMS/PQS development, implementation and remediation for GxP-regulated pharma, biotech and MedTech companies, including FDA QMSR compliance.";
export const QMS_HERO_IMAGE = "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance/qms-implementation-remediation.jpg";

const FRAMEWORKS = ["GMP and PQS (ICH Q10)", "GDP", "GCP", "ISO 13485:2016", "FDA 21 CFR Part 820 (QMSR)", "EU MDR and IVDR", "MDSAP"];

const SCENARIOS = [
  { icon: Building2, title: "Building a QMS for the first time", text: "You lack the conceptual expertise or hands-on experience to develop and implement an efficient, GxP-compliant QMS." },
  { icon: Puzzle, title: "A system that no longer fits", text: "Fragmented processes, unclear responsibilities or outdated documentation make your QMS harder to run and harder to defend." },
  { icon: RefreshCw, title: "Regulatory change", text: "New or revised requirements, such as the FDA's QMSR, call for a maturity assessment and clear modernization priorities." },
  { icon: Eye, title: "An independent view", text: "You need an external party to advise on how to improve or remediate your QMS, and whether it is ready for a regulatory inspection." },
];

const PANELS = [
  {
    title: "QMS development & implementation", img: panelImplImg,
    alt: "A trainer showing new QMS software on a large screen in a training room",
    items: [
      "Drafting of policies, procedures and work instructions based on industry good practice templates",
      "Selection and implementation of suitable QMS software",
      "Awareness training for management and training of personnel",
      "Project management for the QMS roll-out",
    ],
    note: "Available as full-service implementation, coaching and support on your own documentation, or startup assistance to help your team plan its first QMS.",
  },
  {
    title: "QMS remediation & improvement", img: panelRemImg,
    alt: "Two quality professionals reviewing a gap assessment with printed documents and a laptop",
    items: [
      "QMS/PQS maturity and efficiency assessments",
      "Identification of gaps and modernization priorities",
      "Remediation of system-level gaps identified in audits, inspections or self-assessments",
      "Simplification of fragmented processes and outdated documentation",
      "Continuous improvement embedded in your daily operations",
    ],
    noteTitle: "QMSR compliance for medical devices and IVD",
    note: "Align your quality system with the FDA's Quality Management System Regulation.",
  },
];

const STEPS = [
  { title: "Assessment", text: "We analyze your current processes, documentation and QMS maturity, and identify gaps and priorities." },
  { title: "Design", text: "In workshops with your stakeholders, we define requirements and design a practical solution based on your actual processes and responsibilities." },
  { title: "Documentation & implementation", text: "We develop or update procedures, policies and work instructions, and support the selection and roll-out of QMS software." },
  { title: "Training & continuous improvement", text: "We train your teams to adopt and maintain the QMS, and embed improvement practices that keep it efficient and compliant." },
];

const WHY = [
  { title: "Practical QMS design", text: "Systems designed around your actual processes and responsibilities, not generic templates." },
  { title: "Broad range of QMS capabilities", text: "Expertise covering organization, processes, facilities, utilities, IT and equipment." },
  { title: "Cross-disciplinary expertise", text: "QA expertise combined with other QbD Group specialists, such as regulatory, validation and software, where the scope requires it." },
  { title: "Global pool, local presence", text: "QMS experts worldwide with local on-site support capabilities, and remote delivery where it makes sense." },
  { title: "EU and US frameworks", text: "A proven track record across ISO 13485:2016 and FDA 21 CFR Part 820, bridging your EU and US quality systems." },
];


const QmsImplementationRemediation = () => {
  const base = getServiceBySlug("quality-assurance");
  if (!base) return <Navigate to="/en/services" replace />;

  const service: ServiceData = {
    ...base,
    title: "QMS Development & Optimization",
    subtitle: "Build, modernize or remediate a quality system that works in practice",
    heroImage: heroImg,
    description:
      "Whether you are building a quality management system from scratch or improving one that no longer fits your organization, QbD Group supports GxP-regulated pharma, biotech and MedTech companies with practical QMS/PQS development, implementation and remediation, designed around your actual processes and responsibilities.",
  };

  return (
    <>
      <SEOHead title={QMS_META_TITLE} description={QMS_META_DESCRIPTION} ogImage={QMS_HERO_IMAGE} canonical="https://qbdgroup.com/en/services/quality-assurance/qms-development-optimization" />
      <BreadcrumbJsonLd
        items={[
          { name: "Services", path: "/en/services" },
          { name: "Quality Assurance", path: "/en/services/quality-assurance" },
          { name: "QMS Development & Optimization" },
        ]}
      />
      <ScrollToTop />
      <Navbar />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ "--service-color": service.color } as React.CSSProperties}>
        <ServiceHero
          eyebrow="Quality Assurance"
          service={service}
          subtitleClassName="text-xl sm:text-2xl"
          parentCrumbs={[{ label: "Quality Assurance", to: "/en/services/quality-assurance" }]}
          discoverButtonTarget="how-we-help"
          discoverButtonLabel="Discover our offerings"
          discoverButtonIcon={ArrowDown}
          onCtaClick={() => { window.location.href = "/en/contact"; }}
          primaryCtaLabel="Get in touch"
        />

        {/* What is */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <Eyebrow>The essentials</Eyebrow>
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-6">What is QMS implementation and remediation?</h2>
              <p className="text-lg text-qa-copy-body leading-[1.6] mb-8">
                A quality management system (QMS), or pharmaceutical quality system (PQS), is the framework of policies, processes, responsibilities and records that ensures the quality and safety of your products and your compliance with regulatory requirements. QMS implementation covers developing and rolling out that framework: from policies and procedures to QMS software and personnel training. QMS remediation covers assessing an existing system, identifying gaps and modernization priorities, and bringing it back to an efficient, compliant state.
              </p>
              <p className="text-xs font-semibold uppercase tracking-wider text-qa-copy-ink mb-3">Frameworks we work with</p>
              <ul className="flex flex-wrap gap-2">
                {FRAMEWORKS.map((f) => (
                  <li key={f} className="rounded-full bg-qa-copy-accent/15 px-4 py-1.5 text-sm font-medium text-qa-copy-ink">{f}</li>
                ))}
              </ul>
            </div>
            <img src={essentialsImg} alt="A team mapping a quality system process flow on a whiteboard" loading="lazy" width={1200} height={896} className="w-full aspect-[4/3] object-cover rounded-[16px]" />
          </div>
        </section>

        {/* When */}
        <section className="py-16 md:py-24 bg-qa-copy-surface">
          <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
            <Eyebrow>Common scenarios</Eyebrow>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-10">When do companies need support?</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {SCENARIOS.map(({ icon: Icon, title, text }) => (
                <article key={title} className="flex h-full flex-col rounded-[16px] border border-qa-copy-border/50 bg-card p-8 shadow-sm">
                  <Icon size={26} strokeWidth={1.5} aria-hidden="true" className="mb-4 text-qa-copy-accent" />
                  <h3 className="text-base font-semibold text-foreground mb-3">{title}</h3>
                  <p className="text-sm text-qa-copy-body leading-relaxed">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* How we help */}
        <section id="how-we-help" className="scroll-mt-28 py-16 md:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <Eyebrow>Our services</Eyebrow>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-10">How we help</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {PANELS.map(({ title, img, alt, items, noteTitle, note }) => (
                <article key={title} className="flex h-full flex-col overflow-hidden rounded-[16px] border border-qa-copy-border/50 bg-card shadow-sm">
                  <img src={img} alt={alt} loading="lazy" width={1408} height={704} className="w-full aspect-[2/1] object-cover" />
                  <div className="flex flex-1 flex-col p-8">
                    <h3 className="text-lg font-semibold text-foreground mb-5">{title}</h3>
                    <ul className="space-y-3 mb-6">
                      {items.map((it) => (
                        <li key={it} className="flex gap-3 text-sm text-qa-copy-body-strong leading-relaxed">
                          <CheckCircle2 size={18} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0 text-qa-copy-accent" />
                          {it}
                        </li>
                      ))}
                    </ul>
                    {note && (
                      <div className="mt-auto rounded-[12px] bg-qa-copy-accent/15 p-5">
                        {noteTitle && (
                          <h4 className="text-sm font-semibold text-qa-copy-accent-strong mb-2">{noteTitle}</h4>
                        )}
                        <p className="text-sm font-medium text-qa-copy-ink leading-relaxed">{note}</p>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* QMSR band */}
        <section className="py-16 md:py-24 bg-qa-copy-surface">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <div className="flex flex-col-reverse overflow-hidden rounded-[16px] bg-qa-copy-ink md:grid md:grid-cols-[3fr_2fr]">
              <div className="p-8 md:p-12">
                <Eyebrow light>Medical devices &amp; IVD</Eyebrow>
                <h2 className="text-2xl sm:text-3xl font-semibold text-primary-foreground mb-3">QMSR compliance</h2>
                <p className="text-lg text-primary-foreground/85 mb-6">Align your quality system with the FDA's new rule and stay inspection-ready in the US market.</p>
                <p className="text-base text-primary-foreground/70 leading-relaxed mb-4">
                  The FDA's Quality Management System Regulation (QMSR) replaced 21 CFR Part 820 and now incorporates ISO 13485:2016 by reference, with FDA-specific requirements layered on top.
                </p>
                <p className="text-base text-primary-foreground/70 leading-relaxed mb-8">
                  QbD Group helps medical device and IVD manufacturers assess their system, close the gaps and stay inspection-ready, whether already ISO 13485 certified or building toward US market entry for the first time.
                </p>
                <Button asChild size="lg" className="rounded-full bg-qa-copy-accent text-qa-copy-ink hover:bg-qa-copy-accent/90">
                  <Link to="/en/services/quality-assurance/qmsr-compliance">Get QMSR-ready<ArrowRight aria-hidden="true" /></Link>
                </Button>
              </div>
              <img src={qmsrImg} alt="A cleanroom technician inspecting a medical implant component under a magnifier" loading="lazy" width={1008} height={1008} className="w-full h-64 md:h-full object-cover" />
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <Eyebrow>Four steps</Eyebrow>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-10">Our process</h2>
            <img src={processImg} alt="Quality, production and management stakeholders mapping a process with sticky notes in a workshop" loading="lazy" width={1920} height={640} className="w-full aspect-[16/9] md:aspect-[3/1] object-cover rounded-[16px] mb-12" />
            <ol className="relative grid gap-8 md:grid-cols-4 md:gap-6">
              <span aria-hidden="true" className="hidden md:block absolute top-5 left-[12.5%] right-[12.5%] h-px bg-qa-copy-accent/50" />
              <span aria-hidden="true" className="md:hidden absolute top-5 bottom-5 left-5 w-px bg-qa-copy-accent/50" />
              {STEPS.map((s, i) => (
                <li key={s.title} className="relative flex gap-5 md:flex-col md:items-center md:text-center md:gap-0">
                  <span className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full bg-qa-copy-accent text-sm font-bold text-qa-copy-ink md:mb-5">{i + 1}</span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-2">{s.title}</h3>
                    <p className="text-sm text-qa-copy-body leading-relaxed">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Why QbD */}
        <section className="py-16 md:py-24 bg-qa-copy-ink">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <div className="mb-12">
              <div>
                <Eyebrow light>Why QbD Group</Eyebrow>
                <h2 className="text-2xl sm:text-3xl font-semibold text-primary-foreground mb-6">Your trusted QMS partner</h2>
                <p className="max-w-[760px] text-lg text-primary-foreground/75 leading-[1.6] mb-8">With a 20+ year track record as a QMS partner, QbD Group is a preferred supplier for Top 20 Pharma and MedTech companies.</p>
                <Button asChild size="lg" className="rounded-full bg-qa-copy-accent text-qa-copy-ink hover:bg-qa-copy-accent/90">
                  <Link to="/en/contact">Partner with us<ArrowRight aria-hidden="true" /></Link>
                </Button>
              </div>
            </div>
            <div className="grid gap-6 auto-rows-fr sm:grid-cols-2 lg:grid-cols-6">
              {WHY.map((w, i) => (
                <div key={w.title} className={cn("flex flex-col rounded-[16px] border border-primary-foreground/10 bg-primary-foreground/5 p-6 lg:col-span-2", i === 3 && "lg:col-start-2")}>
                  <CheckCircle2 size={22} strokeWidth={1.5} className="text-qa-copy-accent mb-3" />
                  <h3 className="text-base font-semibold text-primary-foreground mb-2">{w.title}</h3>
                  <p className="text-sm text-primary-foreground/70 leading-relaxed">{w.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ContactCTA title={base.formHeading} description={base.formSubtitle} ctaLabel="Contact us" />
      </main>
      <Footer />
    </>
  );
};

export default QmsImplementationRemediation;
